import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPayDocumentsAction } from '../../modules/actions';
import styled from 'styled-components';
import { getTranslationKey } from '../../modules/utils';
import { getPayDocuments, getPayDocumentsNormalized } from '../../modules/selectors';
import { Title } from './common';
import { FiCheckCircle, FiClock, FiDownload, FiInfo } from 'react-icons/fi';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import theme from '../../assets/css/theme';
import { Badge, Button } from 'reactstrap';
import { PAY_DOCUMENT_STATUS } from '../../modules/types';
import { Paginator } from '../../elements/Paginator';
import media from '../../assets/css/media';
import { downloadInvoiceWithErrorCheck } from '../../helpers/invoiceDownload';

const StyledBadge = styled(Badge)`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  padding: 0.25rem 0.5rem;

  font-weight: normal;
  color: #1d1d1d;

  &.badge-success {
    background-color: #d2e8d5;
  }

  &.badge-danger {
    background-color: #f8e5e5;
  }

  &.badge-warning {
    background-color: #f0edd1;
  }

  &.badge-info {
    background-color: #a1dff1;
  }
`;

function PayDocumentStatus({ status }: { status: PAY_DOCUMENT_STATUS }) {
  const statusText = getTranslationKey(`shop.paymentSettings.documents.statuses.${status}`);
  const iconSize = 15;
  switch (status) {
    case PAY_DOCUMENT_STATUS.FAILED:
      return (
        <StyledBadge pill color="danger">
          <FiInfo size={iconSize} className="mr-1" />
          {statusText}
        </StyledBadge>
      );
    case PAY_DOCUMENT_STATUS.PAID:
      return (
        <StyledBadge pill color="success">
          <FiCheckCircle size={iconSize} className="mr-1" />
          {statusText}
        </StyledBadge>
      );
    case PAY_DOCUMENT_STATUS.PENDING:
      return (
        <StyledBadge pill color="warning">
          <FiClock size={iconSize} className="mr-1" />
          {statusText}
        </StyledBadge>
      );
    case PAY_DOCUMENT_STATUS.OPEN:
      return (
        <StyledBadge pill color="info">
          <FiClock size={iconSize} className="mr-1" />
          {statusText}
        </StyledBadge>
      );
    default:
      return null;
  }
}

const PAGE_SIZE = 15;
function useShopDocumets() {
  const dispatch = useDispatch();
  const payDocuments = useSelector(getPayDocuments);
  const totalPages = Math.ceil(payDocuments.meta.totalAmount / PAGE_SIZE);
  const docs = useSelector(getPayDocumentsNormalized);
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(
      fetchPayDocumentsAction({
        limit: PAGE_SIZE,
        skip: PAGE_SIZE * page
      })
    );
  }, [page]);

  console.log('payDocuments', payDocuments);

  return {
    docs,
    page,
    totalPages,
    setPage
  };
}

const TableWrapper = styled.div`
  @media all and (max-width: ${media.mobile}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;

  @media all and (max-width: ${media.mobile}) {
    table-layout: auto;
    width: auto;
    min-width: 100%;
  }
`;

const StyledHeaderTr = styled.tr`
  border-bottom: 1px solid #1d1d1d;
  text-transform: uppercase;
`;

const StyledTr = styled.tr``;

const tableCellStyling = `
  &:nth-child(1) {
    width: 15%;
  }

  &:nth-child(2) {
    width: 30%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 20px;
  }

  &:nth-child(3) {
    width: 40%;
    padding-right: 20px;
  }

  &:nth-child(4) {
    width: 12%;
    padding-right: 20px;
  }

  @media all and (max-width: ${media.mobile}) {
    width: auto !important;
    white-space: nowrap;
    padding-right: 15px;
    min-width: fit-content;
  }
`;

const StyledTh = styled.th`
  padding: 20px 0;
  font-weight: normal;
  ${tableCellStyling};
`;

const StyledTd = styled.td`
  border-bottom: 1px dotted #c6c6c6;
  padding: 14px 0;
  ${tableCellStyling};
`;

const StyledDownload = styled(FiDownload)`
  cursor: pointer;
  color: #000;
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const InfoMsgWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${theme.buttonsColors.danger};
`;

const DocumentsTab = () => {
  const dispatch = useDispatch();
  const { docs, page, totalPages, setPage } = useShopDocumets();

  const getInvoiceTitle = useCallback(
    ({ reason, orderNumbers }: { reason: string; orderNumbers: number[] }) =>
      getTranslationKey(`shop.${reason}Documents`, {
        orderNumbers: orderNumbers.join(',')
      }),
    []
  );

  const handleDownload = (url: string) => downloadInvoiceWithErrorCheck({ url, dispatch });

  return (
    <div className="new-design">
      <Title className="mb-4">{getTranslationKey('shop.paymentSettings.documents.title')}</Title>
      <TableWrapper>
        <StyledTable>
          <thead>
            <StyledHeaderTr>
              <StyledTh>{getTranslationKey('shop.paymentSettings.documents.date')}</StyledTh>
              <StyledTh>{getTranslationKey('shop.paymentSettings.documents.document')}</StyledTh>
              <StyledTh>{getTranslationKey('shop.paymentSettings.documents.info')}</StyledTh>
              <StyledTh>{getTranslationKey('shop.paymentSettings.documents.status')}</StyledTh>
              <StyledTh />
            </StyledHeaderTr>
          </thead>
          <tbody>
            {docs?.map(
              (
                { date, url, orderNumbers, reason, status, blocksAccountUntilPaid, creditNoteType },
                idx
              ) => {
                const showRequiredActionMsg =
                  [PAY_DOCUMENT_STATUS.OPEN, PAY_DOCUMENT_STATUS.FAILED].includes(status) &&
                  blocksAccountUntilPaid;

                return (
                  <StyledTr key={idx}>
                    <StyledTd>{new Date(date).toLocaleDateString()}</StyledTd>
                    <StyledTd title={getInvoiceTitle({ reason, orderNumbers })}>
                      {getInvoiceTitle({ reason, orderNumbers })}
                    </StyledTd>
                    <StyledTd>
                      {status === PAY_DOCUMENT_STATUS.PAID && creditNoteType && (
                        <>
                          {getTranslationKey(
                            `shop.paymentSettings.documents.creditNote${creditNoteType}`
                          )}
                        </>
                      )}
                      {showRequiredActionMsg && (
                        <InfoWrapper>
                          <InfoMsgWrapper
                            title={getTranslationKey(
                              'shop.paymentSettings.documents.paymentFailureInfo'
                            )}
                          >
                            <IoIosInformationCircleOutline size={20} className="mr-1" />
                            {getTranslationKey('shop.paymentSettings.documents.paymentFailureInfo')}
                          </InfoMsgWrapper>
                          <Button
                            color="primary"
                            outline
                            className="px-3"
                            size="sm"
                            onClick={() => handleDownload(url)}
                          >
                            {getTranslationKey('shop.paymentSettings.documents.payInvoice')}
                          </Button>
                        </InfoWrapper>
                      )}
                    </StyledTd>
                    <StyledTd>
                      <PayDocumentStatus status={status} />
                    </StyledTd>
                    <StyledTd>
                      <StyledDownload size={19} onClick={() => handleDownload(url)} />
                    </StyledTd>
                  </StyledTr>
                );
              }
            )}
          </tbody>
        </StyledTable>
      </TableWrapper>
      <Paginator
        onPageChange={setPage}
        currentPage={page}
        pageButtonLimit={5}
        numPages={totalPages}
        nextPageLabel={getTranslationKey('nextPageLabel')}
        previousPageLabel={getTranslationKey('previousPageLabel')}
      />
    </div>
  );
};

export { DocumentsTab };
